import { useEffect, useState, type ReactNode } from "react";
import { Button, Dialog, TextField, vars } from "@pack-it-up/ui-web";
import { errorToMessage } from "@pack-it-up/viewmodel";
import { useStores } from "../../app/AppContext.js";

// With `hasPassword` false (provider-only account) the modal sets an initial
// password instead: no current-password field, and it calls setPassword — which
// links the password credential so the OAuth provider can later be unlinked.
export const ChangePasswordModal = ({
  open,
  onClose,
  hasPassword = true,
}: {
  open: boolean;
  onClose: () => void;
  hasPassword?: boolean;
}): ReactNode => {
  const { auth } = useStores();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setCurrent("");
      setNext("");
      setConfirm("");
      setError(null);
      setSuccess(false);
    }
  }, [open]);

  const valid = (!hasPassword || current !== "") && next.length >= 8 && confirm !== "";

  const onSubmit = async (): Promise<void> => {
    setError(null);
    if (next !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    const r = hasPassword
      ? await auth.getState().changePassword(current, next)
      : await auth.getState().setPassword(next);
    if (r.ok) {
      setSuccess(true);
      setTimeout(onClose, 800);
    } else {
      setError(errorToMessage(r.error));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={hasPassword ? "Change password" : "Set password"}
      subtitle="Use at least 8 characters."
      compact
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!valid} onClick={() => void onSubmit()}>
            Save password
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: vars.space.md }}>
        {hasPassword && (
          <TextField
            label="Current password"
            type="password"
            value={current}
            onChange={setCurrent}
            autoComplete="current-password"
          />
        )}
        <TextField
          label="New password"
          type="password"
          value={next}
          onChange={setNext}
          autoComplete="new-password"
        />
        <TextField
          label="Confirm new password"
          type="password"
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
        />
        {error !== null && (
          <div role="alert" style={{ fontSize: vars.font.sizeSm, color: vars.color.danger }}>
            {error}
          </div>
        )}
        {success && (
          <div role="status" style={{ fontSize: vars.font.sizeSm, color: vars.color.success }}>
            Password updated.
          </div>
        )}
      </div>
    </Dialog>
  );
};
